import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "@/components/ui/use-toast";
import { api } from "@/lib/api";
import { useRowMutation } from "@/lib/use-api-mutation";
import { Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

/**
 * The mailbox inbox: recent messages (read + unread), each with a "Run
 * analysis" button so the admin can analyse any of them by hand. New unread
 * mail is also analysed automatically by the poller.
 */

type EmailItem = {
  uid: number;
  subject: string;
  fromAddress: string;
  date: string | null;
  analysisId: string | null;
};

export function Mailbox() {
  const { t } = useTranslation();
  const [emails, setEmails] = useState<EmailItem[]>([]);
  const [enabled, setEnabled] = useState(false);
  const analyse = useRowMutation(
    (uid: number) =>
      // Fetching the email over IMAP + running the analysis can take minutes,
      // so raise the default 12 s client timeout (it was cancelling the call).
      api.post<{ analysisId: string | null }>(
        `/api/auto-sources/emails/${uid}/analyze`,
        {},
        { timeoutMs: 10 * 60_000 },
      ),
    {
      rowKey: (uid) => String(uid),
      onSuccess: (res, uid) => {
        setEmails((prev) =>
          prev.map((e) => (e.uid === uid ? { ...e, analysisId: res.analysisId } : e)),
        );
        toast({ title: t("analysisTools.mailbox.analysed") });
      },
      onError: () =>
        toast({ title: t("analysisTools.mailbox.analyseFailed"), variant: "destructive" }),
    },
  );

  const load = useCallback(
    () =>
      api
        .get<{ enabled: boolean; emails: EmailItem[] }>("/api/auto-sources/emails")
        .then((r) => {
          setEnabled(r.enabled);
          setEmails(r.emails);
        })
        .catch(() => {}),
    [],
  );

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Card>
      <CardContent className="space-y-3 py-5">
        {!enabled ? (
          <p className="text-sm text-muted-foreground" data-testid="mailbox-off">
            <Trans i18nKey="analysisTools.mailbox.off" components={{ code: <code /> }} />
          </p>
        ) : emails.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("analysisTools.mailbox.none")}</p>
        ) : (
          <ul className="divide-y rounded-md border">
            {emails.map((e) => (
              <li
                key={e.uid}
                className="flex flex-wrap items-center justify-between gap-3 px-3 py-2"
                data-testid="email-row"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{e.subject}</div>
                  <div className="truncate text-xs text-muted-foreground">{e.fromAddress}</div>
                </div>
                {e.analysisId ? (
                  <Link
                    to="/analysis/history/$id"
                    params={{ id: e.analysisId }}
                    className="text-sm underline"
                    data-testid="email-view"
                  >
                    {t("analysisTools.mailbox.viewAnalysis")}
                  </Link>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={analyse.isRunning(String(e.uid))}
                    onClick={() => analyse.run(e.uid)}
                    data-testid="email-run"
                  >
                    {analyse.isRunning(String(e.uid))
                      ? t("analysisTools.mailbox.analysing")
                      : t("analysisTools.mailbox.run")}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
